import { useState, useCallback, useEffect } from "react";
import type { UseAdminAppReturn } from "./useAdminApp";

export interface TraceEvent {
  accountId: string;
  workspaceId: string;
  sessionId: string;
  seq: number;
  eventType: string;
  eventTime: string;
  subject: string;
  summary: string;
  detail: string | null;
  createdAt: string;
}

export interface SessionSummary {
  sessionId: string;
  eventCount: number;
  firstEventTime: string;
  lastEventTime: string;
}

export interface TraceAdminHandler {
  events: TraceEvent[];
  sessions: SessionSummary[];
  maxSeq: number;
  loading: boolean;
  error: string | null;

  queryTraces: (accountId: string, sessionId: string, startSeq: number, endSeq: number, includeDetails: boolean, eventType?: string) => Promise<void>;
  getMaxSeq: (accountId: string, sessionId: string) => Promise<number>;
  listSessions: (accountId: string) => Promise<void>;
  deleteSession: (accountId: string, sessionId: string) => Promise<void>;
  deleteWorkspace: (accountId: string) => Promise<void>;
}

export function useTraceAdmin(adminApp: UseAdminAppReturn): TraceAdminHandler {
  const [events, setEvents] = useState<TraceEvent[]>([]);
  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  const [maxSeq, setMaxSeq] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const baseFetch = useCallback(async (path: string, options?: RequestInit) => {
    const url = `${adminApp.httpUrl}/api/cloud-provider/${path}`;
    const res = await fetch(url, {
      ...options,
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "X-Workspace-Id": adminApp.workspaceId,
        ...(options?.headers || {}),
      },
    });
    if (!res.ok) {
      const body = await res.text().catch(() => "");
      throw new Error(`${res.status}: ${body}`);
    }
    if (res.status === 204) return null;
    return res.json();
  }, [adminApp.httpUrl, adminApp.workspaceId]);

  const queryTraces = useCallback(async (accountId: string, sessionId: string, startSeq: number, endSeq: number, includeDetails: boolean, eventType?: string) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        sessionId,
        startSeq: String(startSeq),
        endSeq: String(endSeq),
        includeDetails: String(includeDetails),
      });
      if (eventType) params.set("eventType", eventType);
      const data = await baseFetch(`workspace/${accountId}/${adminApp.workspaceId}/traces?${params}`);
      setEvents(data || []);
    } catch (err) {
      setError(`Failed to query traces: ${err instanceof Error ? err.message : String(err)}`);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [baseFetch, adminApp.workspaceId]);

  const getMaxSeq = useCallback(async (accountId: string, sessionId: string): Promise<number> => {
    try {
      const data = await baseFetch(`workspace/${accountId}/${adminApp.workspaceId}/traces/max-seq?sessionId=${encodeURIComponent(sessionId)}`);
      const seq = data?.maxSeq ?? 0;
      setMaxSeq(seq);
      return seq;
    } catch (err) {
      setError(`Failed to get max seq: ${err instanceof Error ? err.message : String(err)}`);
      return 0;
    }
  }, [baseFetch, adminApp.workspaceId]);

  const listSessions = useCallback(async (accountId: string) => {
    setLoading(true);
    setError(null);
    try {
      const data = await baseFetch(`workspace/${accountId}/${adminApp.workspaceId}/traces/sessions`);
      setSessions(data || []);
    } catch (err) {
      setError(`Failed to list sessions: ${err instanceof Error ? err.message : String(err)}`);
      setSessions([]);
    } finally {
      setLoading(false);
    }
  }, [baseFetch, adminApp.workspaceId]);

  const deleteSession = useCallback(async (accountId: string, sessionId: string) => {
    setLoading(true);
    setError(null);
    try {
      await baseFetch(`workspace/${accountId}/${adminApp.workspaceId}/traces/${encodeURIComponent(sessionId)}`, {
        method: "DELETE",
      });
      // Refresh sessions list
      await listSessions(accountId);
    } catch (err) {
      setError(`Failed to delete session traces: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setLoading(false);
    }
  }, [baseFetch, adminApp.workspaceId, listSessions]);

  const deleteWorkspace = useCallback(async (accountId: string) => {
    setLoading(true);
    setError(null);
    try {
      await baseFetch(`workspace/${accountId}/${adminApp.workspaceId}/traces`, {
        method: "DELETE",
      });
      setSessions([]);
      setEvents([]);
      setMaxSeq(0);
    } catch (err) {
      setError(`Failed to delete workspace traces: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setLoading(false);
    }
  }, [baseFetch, adminApp.workspaceId]);

  // Subscribe to trace WSS channel. The AdminPanel toast effect iterates
  // activeSubscriptions and attaches its own toast handler.
  useEffect(() => {
    const traceChannel = `trace.${adminApp.workspaceId}`;
    const unsub = adminApp.subscribeToChannel(traceChannel, () => {
      // Handler is a noop — the AdminPanel toast effect provides the real handler.
    });
    return unsub;
  }, [adminApp.workspaceId, adminApp.subscribeToChannel]);

  return {
    events,
    sessions,
    maxSeq,
    loading,
    error,
    queryTraces,
    getMaxSeq,
    listSessions,
    deleteSession,
    deleteWorkspace,
  };
}
